<template>
    <div>
        <h4 class="mb-3" :class="{ 'mt-3': currentUser?.Staff }">
            <router-link v-if="currentUser?.Staff" to="/logout" class="btn btn-secondary btn-sm ms-2 float-end">
                <i class="fas fa-right-from-bracket"></i>
                Logout
            </router-link>
            <Layers :size="20" /> &nbsp;
            <nobr>Project Status</nobr>
        </h4>
        
        <!-- KtDatatable Implementation -->
        <kt-datatable
            :model="projectModel"
            :data="rows"
            :filter="datatableFilter"
            :meta="{ tableClass: 'table-bordered', rowClass: (row) => getRowClass(row), showFooter: false, rowDraggable: currentFilter.owner.length === 1 }"
            :update-function="handleDatatableUpdate"
            :hide-search="false"
            @cell-click="handleCellClick"
            @row-contextmenu="handleRowContextMenu"
            @row-dragdrop="handleRowDragDrop"
            storage-key="ProjectStatusDatatable"
            :user-scoped="true"
        >
            <template #filters="scope" v-if="mode == 'horz'">
                <div class="col">
                    <div class="row">
                        <div class="col-auto">
                            <client-multi-check 
                                label="Clients"
                                :model-value="currentFilter.client"
                                :filter="clientFilterFn"
                                @update:model-value="(value) => setFilter({client: value})"
                            ></client-multi-check>
                        </div>
                        <div class="col-auto">
                            <tech-multi-check
                                label="Owner"
                                :model-value="currentFilter.owner"
                                :filter="ownerFilterFn"
                                @update:model-value="(value) => setFilter({owner: value})"
                            ></tech-multi-check>
                        </div>
                        <div class="col-auto">
                            <job-status-multi-check 
                                label="Status"
                                :model-value="currentFilter.status"
                                :filter="statusFilterFn"
                                @update:model-value="(value) => setFilter({status: value})"
                            ></job-status-multi-check>
                        </div>
                        <div class="col-auto">
                            <department-multi-check
                                label="Department"
                                :model-value="currentFilter.department"
                                :filter="departmentFilterFn"
                                @update:model-value="(value) => setFilter({department: value})"
                            ></department-multi-check>
                        </div>
                    </div>
                </div>
            </template>

            <template #table-before="scope" v-if="mode == 'vert'">
                <div>
                    <client-multi-check
                        mode="box" 
                        label="Clients"
                        :model-value="currentFilter.client"
                        :filter="clientFilterFn"
                        @update:model-value="(value) => setFilter({client: value})"
                    ></client-multi-check>
                </div>
                <div>
                    <tech-multi-check
                        mode="box"
                        label="Owner"
                        :model-value="currentFilter.owner"
                        :filter="ownerFilterFn"
                        @update:model-value="(value) => setFilter({owner: value})"
                    ></tech-multi-check>
                </div>
                <div>
                    <job-status-multi-check 
                        mode="box"
                        label="Status"
                        :model-value="currentFilter.status"
                        :filter="statusFilterFn"
                        @update:model-value="(value) => setFilter({status: value})"
                    ></job-status-multi-check>
                </div>
                <div>
                    <department-multi-check
                        mode="box"
                        label="Department"
                        :model-value="currentFilter.department"
                        :filter="departmentFilterFn"
                        @update:model-value="(value) => setFilter({department: value})"
                    ></department-multi-check>
                </div>
            </template> 

            <template #after-search="scope">
                <div class="-btn-group">

                    <button @click="toggleMode()" class="btn btn-sm btn-outline-primary me-2">
                        <i :class="mode == 'horz' ? 'fa fa-arrows-alt-h' : 'fa fa-arrows-alt-v'"></i>
                    </button>

                    <button @click="refresh()" 
                        class="btn btn-sm btn-outline-secondary me-2"
                    >Refresh <i class="fa fa-sync"></i></button>
                </div>
            </template>

            <template #view_string="scope">
                <span v-if="scope.column.name === 'Id'">
                    <a href="javascript:void(0);" @click="editRecord(scope.row.Project_ID)">
                        {{ scope.row.Project_ID }}
                    </a>
                </span>
                <span v-if="scope.column.name === 'Owner'">
                    <a href="javascript:void(0)">
                        {{ getOwner(scope.row.Owner) }}
                    </a>
                </span>
                <span v-else-if="scope.column.name === 'Name'">
                    <a href="javascript:void(0);" @click="editRecord(scope.row.Project_ID)">
                        {{ scope.row.RequestDescription || 'Not Set' }}
                    </a>
                </span>
                <span v-else-if="scope.column.name === 'Client'">
                    {{ scope.row.Company || 'Not Set' }}
                </span>
                <span v-else-if="scope.column.name === 'Status'">
                    {{ scope.row.Status_To_Customer || 'Not Set' }}
                </span>
                <span v-else-if="scope.column.name === 'ProjectStatus'">
                    <span :class="getStatusBadgeClass(scope.row.RequestStatus)" class="badge">
                        {{ JobStatusList[scope.row.RequestStatus]?.Status || 'Not Set' }}
                    </span>
                </span>
                <span v-else-if="scope.column.name === 'ProjectInvoices'">
                    <span v-if="scope.row.Project_Invoices">
                        <a
                            v-if="isUrl(scope.row.Project_Invoices)"
                            :href="scope.row.Project_Invoices"
                            target="_blank"
                            class="btn btn-sm btn-outline-primary"
                        >
                            <i class="fa fa-link me-1"></i> View Invoices
                        </a>
                        <span v-else>
                            {{ scope.row.Project_Invoices }}
                        </span>
                    </span>
                    <span v-else class="text-muted">-</span>
                </span>
                <span v-else-if="scope.column.name === 'Report'">
                    <span v-if="scope.row.ServiceReport">
                        <a
                            v-if="isUrl(scope.row.ServiceReport)" 
                            :href="scope.row.ServiceReport" 
                            target="_blank"
                            class="btn btn-sm btn-outline-primary"
                        >
                            <i class="fa fa-link me-1"></i> View Report
                        </a>
                        <span v-else>
                            {{ scope.row.ServiceReport }}
                        </span>
                    </span>
                    <span v-else class="text-muted">-</span>
                </span>
                <span v-else>
                    {{ scope.value }}
                </span>
            </template>

            <template #view_number="scope">
                <span v-if="scope.column.name === 'Priority'">
                    <span :class="getPriorityBadgeClass(scope.row.Priority)" class="badge">
                        {{ scope.row.Priority || 'Not Set' }}
                    </span>
                </span>
                <span v-else-if="scope.column.name === 'Progress'">
                    <div 
                        class="progress" 
                        role="progressbar" 
                        :aria-valuenow="scope.row.Progress" 
                        aria-valuemin="0" 
                        aria-valuemax="100"
                        style="width: 150px; height: 25px;"
                    >
                        <div 
                            class="progress-bar" 
                            :style="getProgressWidth(scope.row.Progress)"
                            :class="getProgressClass(scope.row.Progress)"
                        >{{ scope.row.Progress }}%</div>
                    </div>
                </span>
                <span v-else>
                    {{ scope.value }}
                </span>
            </template>

            <!-- Filter Badges -->
            <template #under-header>
                <div v-if="hasActiveFilters" class="d-flex flex-wrap gap-2 p-2">
                    <span class="text-muted small me-2">Active filters:</span>
                    
                    <!-- Client badges -->
                    <span v-for="clientId in currentFilter.client" :key="'client-' + clientId" 
                          class="badge bg-primary d-flex align-items-center">
                        Client: {{ getClientName(clientId) }}
                        <button type="button" class="btn-close btn-close-white ms-2" 
                                @click="removeClientFilter(clientId)"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Status badges -->
                    <span v-for="statusId in currentFilter.status" :key="'status-' + statusId"
                          class="badge bg-info d-flex align-items-center">
                        Status: {{ getStatusName(statusId) }}
                        <button type="button" class="btn-close btn-close-white ms-2" 
                                @click="removeStatusFilter(statusId)"
                                style="font-size: 0.7em;"></button>
                    </span>

                    <!-- Department badges -->
                    <span v-for="dept in currentFilter.department" :key="'dept-' + dept"
                          class="badge bg-secondary d-flex align-items-center">
                        Department: {{ dept }}
                        <button type="button" class="btn-close btn-close-white ms-2" 
                                @click="removeDepartmentFilter(dept)"
                                style="font-size: 0.7em;"></button>
                    </span>

                    <!-- Owner badges -->
                    <span v-for="owner in currentFilter.owner" :key="'owner-' + owner"
                          class="badge bg-secondary d-flex align-items-center">
                        Owner: {{ TechList[owner].TechnicianName }}
                        <button type="button" class="btn-close btn-close-white ms-2" 
                                @click="removeOwnerFilter(owner)"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Search term badge -->
                    <span v-if="currentFilter.searchTerm"
                          class="badge bg-secondary d-flex align-items-center">
                        Search: {{ currentFilter.searchTerm }}
                        <button type="button" class="btn-close btn-close-white ms-2" 
                                @click="setFilter({ searchTerm: '' })"
                                style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Clear all filters -->
                    <button v-if="hasActiveFilters" 
                            class="btn btn-sm btn-outline-secondary ms-0" 
                            @click="clearAllFilters()">
                        Clear All
                    </button>
                </div>
            </template>
        </kt-datatable>

        <!-- Row Context Menu -->
        <kt-context-menu
            ref="contextMenuRef"
            :items="contextMenuItems"
            min-width="200px"
            @select="handleContextMenuSelect"
        />

        <!-- Edit Project Modal -->
        <kt-modal
            :visible="showModal"
            :title="'Edit Project'"
            @dismiss="showModal = false"
            size="modal-lg"
        >
            <template #body>
                <div class="container-fluid" v-if="form">
                    
                    <div class="row mb-3">
                        <div class="col-md-6">
                            <label class="form-label d-block">
                                <span class="d-block"><strong>Project Name</strong></span>
                                <input
                                    type="text"
                                    class="form-control form-control-sm"
                                    v-model="form.RequestDetailedDescription"
                                    placeholder="Project name..."
                                />
                            </label>
                        </div>
                        <div class="col-md-6">
                            <label class="form-label d-block mb-1"><strong>Department / Category</strong></label>
                            <div class="input-group input-group-sm">
                                <select
                                    v-if="departmentOptions.length > 0"
                                    class="form-select form-select-sm"
                                    v-model="form.Department"
                                >
                                    <option :value="undefined">Select Department...</option>
                                    <option
                                        v-for="dept in departmentOptions"
                                        :key="dept.value"
                                        :value="dept.value"
                                    >{{ dept.label }}</option>
                                </select>
                                <select
                                    v-else
                                    class="form-select form-select-sm"
                                    disabled
                                >
                                    <option>Use button to add department</option>
                                </select>
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-primary"
                                    @click="openDepartmentSelector"
                                    title="Manage departments"
                                >
                                    <Search :size="16" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-6">
                            <label class="form-label d-block">
                                <span class="d-block"><strong>Create Date</strong></span>
                                <input
                                    type="date"
                                    class="form-control form-control-sm"
                                    v-model="form.CreateDate"
                                />
                            </label>
                        </div>
                        <div class="col-md-6">
                            <label class="form-label d-block">
                                <span class="d-block"><strong>Complete By Date</strong></span>
                                <input
                                    type="date"
                                    class="form-control form-control-sm"
                                    v-model="form.CompletionDate"
                                />
                            </label>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-md-4">
                            <label class="form-label d-block">
                                <span class="d-block"><strong>Priority</strong></span>
                                <input type="text" class="form-control form-control-sm" v-model="form.Priority" />
                                <!-- 
                                <select class="form-select" v-model="form.Priority">
                                    <option :value="null">-- Select Priority --</option>
                                    <option v-for="priority in PriorityList" :key="priority.Id" :value="priority.Id">
                                        {{ priority.Level }}
                                    </option>
                                </select>
                                -->
                            </label>
                        </div>
                        <div class="col-md-4">
                            <label class="form-block d-block">
                                <span class="d-block"><strong>Request Status</strong></span>
                                <job-status-ac
                                    v-model="form.RequestStatus"
                                    :multiple="false"
                                ></job-status-ac>
                            </label>
                        </div>
                        <div class="col-md-4">
                            <label class="form-label d-block">
                                <span class="d-block"><strong>Progress %</strong></span>
                                <input
                                    type="number"
                                    class="form-control form-control-sm"
                                    v-model="form.Progress"
                                    min="0"
                                    max="100"
                                />
                            </label>
                        </div>
                        <!-- 
                        <div class="col-md-4">
                            <label class="form-label d-block">
                                <span class="d-block"><strong>Project Status</strong></span>
                                <select class="form-select" v-model="form.RequestStatus">
                                    <option :value="null">-- Select Status --</option>
                                    <option v-for="status in JobStatusList" :key="status.Id" :value="status.Id">
                                        {{ status.Status }}
                                    </option>
                                </select>
                            </label>
                        </div>
                        -->
                    </div>

                    <div class="row mb-3">
                        <div class="col-12">
                            <label class="form-label d-block">
                                <span class="d-block"><strong>Current Status</strong></span>
                                <textarea
                                    class="form-control form-control-sm"
                                    v-model="form.TextStatus"
                                    rows="4"
                                    placeholder="Status description..."
                                ></textarea>
                            </label>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-12">
                            <label class="form-label d-block">
                                <span class="d-block"><strong>Service Report</strong></span>
                                <input
                                    type="text"
                                    class="form-control form-control-sm"
                                    v-model="form.ServiceReport"
                                    placeholder="URL or text for service report..."
                                />
                            </label>
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-12">
                            <label class="form-label d-block">
                                <span class="d-block"><strong>Project Invoices</strong></span>
                                <textarea
                                    class="form-control form-control-sm"
                                    v-model="form.Project_Invoices"
                                    rows="3"
                                    placeholder="Invoice URL or reference..."
                                ></textarea>
                            </label>
                        </div>
                    </div>
                </div>
            </template>

            <template #footer>
                <button class="btn btn-secondary" @click="showModal = false">
                    Cancel
                </button>
                <button class="btn btn-primary" @click="updateForm()">
                    Update
                </button>
            </template>
        </kt-modal>

        <!-- Department Selector Modal (same component used by JobForm) -->
        <department-selector-modal
            v-if="showDepartmentSelector"
            :visible="showDepartmentSelector"
            :current-value="(form as any)?.Department"
            :can-manage="isAdmin"
            @close="showDepartmentSelector = false"
            @select="onDepartmentSelected"
            @departments-changed="loadDepartmentOptions"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { useRouter, useRoute } from 'vue-router';
import Api from "@/services/Api";
import ApiService from "@/services/ApiService";
import Helper from "@/services/Helper";
import Cache from "@/services/Cache";
import Emitter from "@/services/Emitter";
import { RequestModel } from "@/models/RequestModel";
import KtModal from '@/components/UI/KtModal.vue';
import KtDatatable from "@/components/UI/KtDatatable.vue";
import KtContextMenu from "@/components/UI/KtContextMenu.vue";
import type { ContextMenuItem } from "@/components/UI/KtContextMenu.vue";
import { KtDatatableModelProp } from "@/components/UI/KtDataTable";
import JobStatusAc from "@/components/Jobs/JobStatusAc.vue";

import { Layers } from 'lucide-vue-next';

import TechMultiCheck from "@/components/Techs/TechMultiCheck.vue"
import ClientMultiCheck from "@/components/Clients/ClientMultiCheck.vue";
import JobStatusMultiCheck from "@/components/Jobs/JobStatusMultiCheck.vue";
import DepartmentMultiCheck from "@/components/Jobs/DepartmentMultiCheck.vue";
import DepartmentSelectorModal from "@/components/Requests/DepartmentSelectorModal.vue";
import { Search } from "lucide-vue-next";
import { usePermissions } from "@/composables/usePermissions";

import { storeToRefs } from "pinia";
import { useClientStore } from '@/stores/ClientStore';
import { useJobStatusStore } from "@/stores/JobStatusStore";
import { usePriorityStore } from "@/stores/PriorityStore";
import { useTechStore } from "@/stores/TechStore";
import optionsService from "@/services/OptionsService";
import { useDatatableSettings, hasNonEmptyFilters } from "@/composables";

// Per-user persistence of filters for this datatable. Column visibility,
// widths and order are persisted by KtDatatable itself via the `user-scoped`
// prop. This composable handles the filter portion so the user's chosen
// clients/status/owner/department/search/sort roam across devices.
const {
    loadSettings: loadDatatableSettings,
    saveFilters: saveDatatableFilters
} = useDatatableSettings('ProjectStatusDatatable');

const jobStatusStore = useJobStatusStore();
const { list: JobStatusList } = storeToRefs(jobStatusStore);

const clientStore = useClientStore();
const { list: ClientList } = storeToRefs(clientStore);

const priorityStore = usePriorityStore();
const { list: PriorityList } = storeToRefs(priorityStore);

const techStore = useTechStore();
const { list: TechList } = storeToRefs(techStore);

// do we have to "reload" all ?
jobStatusStore.reload();
clientStore.reload();
priorityStore.reload();
techStore.reload();

const router = useRouter();
const route = useRoute();

const rows = ref<any[]>([]);
const total = ref(0);
const showModal = ref(false);
const form = ref<RequestModel | null>(null);

const mode = ref("horz");

// Department / Category state for the Edit Project modal. Mirrors the
// mechanism used by JobForm: a master list loaded from the Options table
// under the `departments_json` key, plus a DepartmentSelectorModal the
// user can open to add/manage departments. The selected value is stored
// as the department's name in `form.Department` (matching `model.Department`
// in JobForm).
const { isAdmin, isStaff, currentUser } = usePermissions();
const departmentOptions = ref<{ label: string; value: string }[]>([]);
const showDepartmentSelector = ref(false);
const DEPARTMENT_OPTION_KEY = 'departments_json';

const loadDepartmentOptions = async () => {
    try {
        const optionValue = await optionsService.get(DEPARTMENT_OPTION_KEY);
        if (optionValue) {
            const depts = JSON.parse(optionValue);
            departmentOptions.value = depts.map((d: any) => ({
                value: d.name,
                label: d.name
            }));
        } else {
            departmentOptions.value = [];
        }
    } catch (e) {
        console.error('Failed to load departments:', e);
        departmentOptions.value = [];
    }
};

const openDepartmentSelector = () => {
    showDepartmentSelector.value = true;
};

const onDepartmentSelected = (dept: { name: string }) => {
    if (form.value) {
        (form.value as any).Department = dept.name;
    }
    showDepartmentSelector.value = false;
};

// Tracks whether initial settings have finished loading. Any save issued
// before hydration completes (e.g. the initial update event emitted by
// <kt-datatable> on mount) would stomp the user's saved settings with
// component defaults, which caused the mode to appear to "reset" after a
// hard page refresh but not after in-app navigation (where the settings
// composable's module-level cache served the data synchronously).
const hydrated = ref(false);

// Persist the user's chosen view (filters on top vs. along the side) together
// with their filter state so their layout is restored across sessions/devices.
const persistUserPreferences = () => {
    if (!hydrated.value) return;
    saveDatatableFilters({ ...currentFilter.value, mode: mode.value });
};

const toggleMode = () => 
{
    if (mode.value == "horz") 
    {
        mode.value = "vert";
    }

    else
    {
        mode.value = "horz";
    }

    persistUserPreferences();
};

const contextMenuRef = ref<InstanceType<typeof KtContextMenu> | null>(null);
const contextMenuRow = ref<any>(null);

const contextMenuItems = ref<ContextMenuItem[]>([
    { id: 'owner',      label: 'Filter By Owner',      icon: 'fas fa-user' },
    { id: 'client',     label: 'Filter By Client',     icon: 'fas fa-building' },
    { id: 'status',     label: 'Filter By Status',     icon: 'fas fa-tag' },
    { id: 'department', label: 'Filter By Department', icon: 'fas fa-sitemap' },
]);

// Filter state interface
interface ProjectListFilter {
    client: number[];
    status: number[];
    owner: number[];
    department: string[];
    searchTerm: string;
    page: number;
    size: number;
    sort: Array<Record<string, 'ASC' | 'DESC'>>;
}

// Current filter state
const currentFilter = ref<ProjectListFilter>({
    client: [],
    status: [],
    owner: [],
    department: [],
    searchTerm: '',
    page: 0,
    size: 25,
    sort: []
});

// Datatable filter for pagination
const datatableFilter = computed(() => ({
    page: currentFilter.value.page,
    size: currentFilter.value.size,
    total: total.value,
    search: currentFilter.value.searchTerm,
    sort: currentFilter.value.sort
}));

// Project Status datatable model
const projectModel = computed<Array<KtDatatableModelProp>>(() => [
    {
        name: "Id",
        label: "ID",
        field: "Project_ID",
        display: true,
        sortable: true,
        type: "string",
        width: 80
    },
    {
        name: "Client",
        label: "Client",
        field: "Company",
        display: true,
        sortable: true,
        type: "string",
        width: 200
    },
    {
        name: "Department",
        label: "Department",
        field: "Department",
        display: true,
        sortable: true,
        type: "string",
        width: 150
    },
    {
        name: "Owner",
        label: "Owner",
        field: "Owner",
        display: true,
        sortable: true,
        type: "string",
        width: 150
    },
    {
        name: "Priority",
        label: "Priority",
        field: "Priority",
        display: true,
        sortable: true,
        type: "number",
        width: 100
    },
    {
        name: "Name",
        label: "Project Name",
        field: "RequestDescription",
        display: true,
        sortable: true,
        type: "string"
    },
    {
        name: "Progress",
        label: "Progress",
        field: "Progress",
        display: true,
        sortable: true,
        type: "number",
        width: 180
    },
    {
        name: "Status",
        label: "Status",
        display: true,
        // sortable: true,
        type: "string",
        width: 150
    },
    {
        name: "ProjectStatus",
        label: "Project Status",
        display: true,
        type: "string",
        // sortable: true,
        width: 150
    },
    {
        name: "CompletionDate",
        label: "Completion Date",
        display: true,
        sortable: true,
        type: "date",
        width: 120
    },
    {
        name: "CreateDate",
        label: "Create Date",
        display: true,
        sortable: true,
        type: "date",
        width: 120
    },
    {
        name: "ProjectInvoices",
        label: "Invoices",
        field: "Project_Invoices",
        display: true,
        type: "string",
        width: 180
    },
    {
        name: "Report",
        label: "Report",
        display: true,
        type: "string",
        width: 150
    }
]);

// Check if there are active filters
const hasActiveFilters = computed(() => {
    return (currentFilter.value.client?.length ?? 0) > 0 ||
           (currentFilter.value.status?.length ?? 0) > 0 ||
           (currentFilter.value.department?.length ?? 0) > 0 ||
           (currentFilter.value.owner?.length ?? 0) > 0 ||
           (currentFilter.value.searchTerm ?? '') !== '';
});

const handleRowDragDrop = async (row: any, brow: any, arow: any) => {
    
    await Api.reorderProjectStatus({
        DraggedProjectId: row.Project_ID,
        RowBeforeId: brow?.Project_ID ?? null,
        RowAfterId: arow?.Project_ID ?? null
    });
    loadData();
    
};

// Sets of identifiers that actually appear in the project list. Used to
// restrict each multi-check dropdown so the user only sees options that
// have at least one matching project. Sourced from a single unfiltered
// getProjectStatus fetch so the sets don't shrink when the user applies
// filters (which would trap them in a narrow subset).
const availableClientIds = ref<Set<number>>(new Set());
const availableOwnerIds = ref<Set<number>>(new Set());
const availableStatusIds = ref<Set<number>>(new Set());
const availableDepartments = ref<Set<string>>(new Set());

const loadAvailableFilterOptions = async () => {
    try {
        const rs = await Api.getProjectStatus({}, true /* background */);
        const clients = new Set<number>();
        const owners = new Set<number>();
        const statuses = new Set<number>();
        const departments = new Set<string>();

        (rs || []).forEach((p: any) => {
            if (p?.CompanyNumber != null) clients.add(p.CompanyNumber as number);
            if (p?.Owner != null) owners.add(p.Owner as number);
            if (p?.RequestStatus != null) statuses.add(p.RequestStatus as number);
            if (p?.Department) departments.add(p.Department as string);
        });

        availableClientIds.value = clients;
        availableOwnerIds.value = owners;
        availableStatusIds.value = statuses;
        availableDepartments.value = departments;
    } catch (e) {
        console.warn('Failed to build available filter options:', e);
    }
};

// Until the sets have loaded, each filter returns true so the dropdown
// isn't briefly empty on first paint.
const clientFilterFn = (client: any): boolean => {
    if (availableClientIds.value.size === 0) return true;
    return availableClientIds.value.has(client?.CustomerNumber as number);
};

const ownerFilterFn = (tech: any): boolean => {
    if (availableOwnerIds.value.size === 0) return true;
    return availableOwnerIds.value.has(tech?.Id as number);
};

const statusFilterFn = (status: any): boolean => {
    if (availableStatusIds.value.size === 0) return true;
    return availableStatusIds.value.has(status?.Id as number);
};

const departmentFilterFn = (dept: any): boolean => {
    if (availableDepartments.value.size === 0) return true;
    return availableDepartments.value.has(dept?.value as string);
};

// Load data from API
const loadData = async () => {
    try {
        // Build filter parameters
        const params: any = {
            Page: currentFilter.value.page,
            Size: currentFilter.value.size,
        };
        
        if (currentFilter.value.client && currentFilter.value.client.length > 0) {
            params.Clients = currentFilter.value.client;
        }
        
        if (currentFilter.value.status && currentFilter.value.status.length > 0) {
            params.Status = currentFilter.value.status;
        }
        
        if (currentFilter.value.owner && currentFilter.value.owner.length > 0) {
            params.Owners = currentFilter.value.owner;
        }
        
        if (currentFilter.value.department && currentFilter.value.department.length > 0) {
            params.Departments = currentFilter.value.department;
        }

        if (currentFilter.value.searchTerm) {
            params.SearchTerm = currentFilter.value.searchTerm;
        }

        if (currentFilter.value.sort && currentFilter.value.sort.length > 0) {
            params.Sort = currentFilter.value.sort;
        }
        
        const rs = await Api.getProjectStatus(params);
        rows.value = rs || [];
        total.value = rows.value.length;
    } catch (error) {
        console.error('Failed to load project status:', error);
        rows.value = [];
        total.value = 0;
    }
};

// Refresh data — also re-builds the available-option sets so every
// multi-check dropdown reflects the current server-side project population.
const refresh = () => {
    loadAvailableFilterOptions();
    loadData();
};

// Handle datatable updates (pagination, sorting)
const handleDatatableUpdate = async (filterUpdate: any) => {
    // Ignore updates emitted before the parent has finished hydrating from
    // saved user settings. <kt-datatable> fires an initial updateFilter on
    // mount with its own defaults (page=0, size=25, sort=[], search='')
    // which would otherwise overwrite our just-loaded state and then
    // trigger a save that persists those defaults back to the server.
    if (!hydrated.value) return;

    if (filterUpdate.page !== undefined) currentFilter.value.page = filterUpdate.page;
    if (filterUpdate.size !== undefined) currentFilter.value.size = filterUpdate.size;
    if (filterUpdate.sort !== undefined) currentFilter.value.sort = filterUpdate.sort || [];
    if (filterUpdate.search !== undefined) currentFilter.value.searchTerm = filterUpdate.search;
    
    await Cache.set('project-status-filter', currentFilter.value);
    persistUserPreferences();
    refresh();
};

// Handle row right-click - show context menu
const handleRowContextMenu = (row: any, rowIndex: number, event: MouseEvent) => {
    
    // alert(JSON.stringify(row));
    // alert(JSON.stringify(row));

    console.debug("row", row);

    let owner = row.Owner ? (": " + TechList.value[row.Owner].TechnicianName) : '';
    let client = row.Company ? (": " + row.Company) : '';
    let status = row.Status_In_Database ? (": " + row.Status_In_Database) : '';
    let department = row.Department ? (": " + row.Department) : '';

    // update the menu to match ...
    let opts = [];

    if (owner){
        opts.push({ id: 'owner', label: 'Filter Owner' + owner, icon: 'fas fa-user' });
    }

    if (client) {
        opts.push({ id: 'client', label: 'Filter Client' + client, icon: 'fas fa-building' });
    }

    if (status) {
        opts.push({ id: 'status', label: 'Filter Status' + status, icon: 'fas fa-tag' });
    }

    if (department) {
        opts.push({ id: 'department', label: 'Filter Department' + department, icon: 'fas fa-sitemap' });
    }

    contextMenuItems.value = opts;
    
    contextMenuRow.value = row;
    contextMenuRef.value?.show(event.clientX, event.clientY);
};

// Handle context menu item selection
const handleContextMenuSelect = (item: ContextMenuItem) => {
    const row = contextMenuRow.value;
    if (!row) return;

    switch (item.id) {
        case 'owner':
            currentFilter.value.owner = [row.Owner];
            break;
        case 'client':
            currentFilter.value.client = [row.CompanyNumber];
            break;
        case 'status':
            currentFilter.value.status = [row.RequestStatus];
            break;
        case 'department':
            currentFilter.value.department = [row.Department];
            break;
    }

    loadData();
};

// Handle cell click - Id opens JobEdit, others open project status modal
const handleCellClick = (row: any, column: any) => {

    if (currentUser?.value?.Staff) {
        return;
    }


    if (column.name === 'Id') {
        // Open global JobEdit modal
        editRecord(row.Project_ID);
    } else if (column.name !== 'Report') {
        // Open local project status modal for other columns (except Report)
        openProjectStatusModal(row.Project_ID);
    }
};

// Set filter
const setFilter = async (updates: Partial<ProjectListFilter>) => {
    Object.assign(currentFilter.value, updates);
    currentFilter.value.page = 0; // Reset to first page when filter changes
    await Cache.set('project-status-filter', currentFilter.value);
    persistUserPreferences();
    refresh();
};

// Clear all filters
const clearAllFilters = async () => {
    currentFilter.value = {
        client: [],
        status: [],
        owner: [],
        department: [],
        searchTerm: '',
        page: 0,
        size: 25,
        sort: []
    };
    await Cache.set('project-status-filter', currentFilter.value);
    persistUserPreferences();
    refresh();
};

// Remove individual filters
const removeClientFilter = (clientId: number) => {
    if (currentFilter.value.client) {
        currentFilter.value.client = currentFilter.value.client.filter(id => id !== clientId);
        setFilter(currentFilter.value);
    }
};

const removeStatusFilter = (statusId: number) => {
    if (currentFilter.value.status) {
        currentFilter.value.status = currentFilter.value.status.filter(id => id !== statusId);
        setFilter(currentFilter.value);
    }
};

const removeDepartmentFilter = (dept: string) => {
    if (currentFilter.value.department) {
        currentFilter.value.department = currentFilter.value.department.filter(d => d !== dept);
        setFilter(currentFilter.value);
    }
};

const removeOwnerFilter = (owner: number) => {
    if (currentFilter.value.owner) {
        currentFilter.value.owner = currentFilter.value.owner.filter(id => id !== owner);
        setFilter(currentFilter.value);
    }
};

// Helper functions
const getClientName = (clientId: number) => {
    let val:string = `Client ${clientId}`;

    for (let i = 0; i < ClientList.value.length || 0; i++)
    {
        if (ClientList.value[i].CustomerNumber == clientId)
        {
            val = ClientList.value[i].Company || val;
            break;
        }
    }
    
    return val;
};

const getOwner = (techId:number) => {
    let val:string = `Tech ${techId}`;

    if (TechList.value && TechList.value[techId]) {
        val = TechList.value[techId].TechnicianName || val;
    }

    return val;
};

const getStatusName = (statusId: number) => {
    return JobStatusList.value[statusId]?.Status || `Status ${statusId}`;
};

const getPriorityBadgeClass = (priority: number | undefined) => {
    if (priority && priority >= 4) return 'badge -bg-danger bg-secondary';
    if (priority && priority >= 3) return 'badge -bg-warning bg-info';
    if (priority && priority >= 2) return 'badge -bg-info bg-warning';
    if (priority === 0 || priority === undefined) return 'badge bg-secondary';
    return 'badge -bg-secondary bg-danger';
};

const getStatusBadgeClass = (statusId: number) => {
    // Color code based on status ID
    // You can adjust these based on actual status meanings
    if (statusId === 1) return 'bg-secondary'; // New
    if (statusId === 2) return 'bg-success'; // Work Complete
    if (statusId === 3) return 'bg-info'; // Invoiced
    if (statusId === 4) return 'bg-primary'; // Paid
    return 'bg-secondary';
};

const getProgressWidth = (progress: number) => {
    return `width: ${progress || 0}%`;
};

const getProgressClass = (progress: number) => {
    if (progress >= 100) return 'bg-success';
    if (progress >= 75) return 'bg-info';
    if (progress >= 50) return 'bg-warning';
    return 'bg-danger';
};

const getRowClass = (row: any) => {
    return '';
};

const isUrl = (val: string) => {
    return val && val.indexOf("://") > -1;
};

// Edit record - open global JobEdit modal
const editRecord = (projectId: number) => {
    if (projectId) {
        Emitter.emit('edit:job', projectId);
    }
};

// Open project status modal - local modal for quick edits
const openProjectStatusModal = async (projectId: number) => {
    try {
        const rec = await ApiService.loadRequest(projectId);
        form.value = rec;
        showModal.value = true;
    } catch (error) {
        console.error('Failed to load project:', error);
    }
};

// Update form
const updateForm = async () => {
    if (!form.value) return;
    
    try {
        // Build update object with only the allowed fields
        const updateData = {
            Project_ID: form.value.Id,
            RequestDescription: form.value.RequestDetailedDescription,
            CompletionDate: form.value.CompletionDate,
            Priority: form.value.Priority,
            Progress: form.value.Progress,
            RequestStatus: form.value.RequestStatus,
            Status_To_Customer: form.value.TextStatus,
            ServiceReport: form.value.ServiceReport,
            Project_Invoices: form.value.Project_Invoices,
            Department: (form.value as any).Department
        };
        
        await Api.updateProjectStatus(updateData);
        form.value = null;
        showModal.value = false;
        refresh();
    } catch (error) {
        console.error('Failed to update project:', error);
    }
};

// Initialize on mount. Priority:
//   1. User's saved filters in the TenantOption store (cross-device).
//   2. Local Cache fallback (same-device recency).
//   3. Defaults.
onMounted(async () => {
    let applied = false;

    try {
        const dbSettings = await loadDatatableSettings();
        if (dbSettings?.filters && hasNonEmptyFilters(dbSettings.filters)) {
            const f = dbSettings.filters as Partial<ProjectListFilter> & { mode?: string };
            currentFilter.value = {
                client: f.client || [],
                status: f.status || [],
                owner: f.owner || [],
                department: f.department || [],
                searchTerm: f.searchTerm || '',
                page: 0, // always start on first page
                size: f.size || 25,
                sort: f.sort || []
            };
            // Restore the filter-layout mode (horz/vert) if the user has set it
            if (f.mode === 'horz' || f.mode === 'vert') {
                mode.value = f.mode;
            }
            applied = true;
        }
    } catch (e) {
        console.warn('ProjectStatusDatatable: failed to load user settings', e);
    }

    // Kick off the available-options fetch in parallel with the main load so
    // the multi-check dropdowns are constrained as soon as possible.
    loadAvailableFilterOptions();

    // Load the full department list used by the Edit Project modal.
    loadDepartmentOptions();

    if (!applied) {
        const cached = await Cache.get('project-status-filter');
        if (cached) {
            currentFilter.value = {
                client: cached.client || [],
                status: cached.status || [],
                owner: cached.owner || [],
                department: cached.department || [],
                searchTerm: cached.searchTerm || '',
                page: cached.page || 0,
                size: cached.size || 25,
                sort: cached.sort || []
            };
        }
    }

    // Mark hydration complete BEFORE triggering the first load. Any
    // subsequent update events (pagination/sort/search) from kt-datatable
    // are legitimate user intent and should be persisted.
    hydrated.value = true;

    loadData();
});
</script>

<style scoped>
.progress {
    background-color: #e9ecef;
}
</style>
